const METADATA_PREFIX = 'metadata.';

type MetadataViewLookup = Record<
  string,
  { view_fields?: Array<{ name?: string }> } | undefined
>;

/**
 * Metadata field names currently mounted in the search filters panel: the
 * selected view's fields, or — with no view — the fields that hold a value AND
 * have a loaded field definition (mirroring MetadataFormContainer). Returns an
 * empty set whenever the metadata form isn't rendered (not shown for this
 * user/config, or still loading), so none of its fields are mounted and callers
 * preserve every metadata filter from Redux.
 */
export function getVisibleMetadataFieldNames(params: {
  metadataFormRendered: boolean;
  metadataViewId: string | undefined;
  loadingMetadata: boolean;
  metadataViews: MetadataViewLookup;
  metadataFields: Record<string, unknown>;
  filterValues: Record<string, unknown>;
}): Set<string> {
  const {
    metadataFormRendered,
    metadataViewId,
    loadingMetadata,
    metadataViews,
    metadataFields,
    filterValues,
  } = params;

  if (!metadataFormRendered) {
    return new Set();
  }

  if (!metadataViewId) {
    return new Set(
      Object.keys(filterValues)
        .filter((key) => key.startsWith(METADATA_PREFIX))
        .map((key) => key.slice(METADATA_PREFIX.length))
        .filter((name) => Boolean(metadataFields[name])),
    );
  }

  if (loadingMetadata) {
    return new Set();
  }

  const fieldNames = (metadataViews[metadataViewId]?.view_fields ?? [])
    .map((field) => field.name)
    .filter((name): name is string => Boolean(name));

  return new Set(fieldNames);
}
